// ============================================================
// DockerArchitecture.jsx
// ============================================================
// Junior-dev Notes:
// - Updated to load DockerArchitecture.png from local assets.
// - Brand identity maintained: Docker Blue (#0DB7ED).
// - Clean Code: Using map() for repeated grid items (DRY).
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import StorageIcon from "@mui/icons-material/Storage";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import HubIcon from "@mui/icons-material/Hub";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TerminalIcon from "@mui/icons-material/Terminal";

/* ── Asset Imports ───────────────────────────────────────── */
// Junior Developer: Always import images so the build tool can find them!
import DockerArchitectureImage from "../../../assets/images/DockerArchitecture.png";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "#FFFFFF",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
    backgroundColor: BRAND.surface,
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: `1px solid ${BRAND.border}`,
    height: "100%",
}));

const ArchCard = styled(Card)(({ theme }) => ({
    borderRadius: "16px",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2)
}));

export default function DockerArchitecture() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HEADER SECTION */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        label="Infrastructure · Module 02"
                        sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Docker <span style={{ color: DOCKER_BLUE }}>Architecture</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6, opacity: 0.8 }}>
                        Understanding how the Docker Client, Host, and Registry interact is key to
                        mastering container orchestration. It uses a Client-Server architecture.
                    </Typography>
                </Box>

                {/* 2. THE BIG PICTURE DIAGRAM */}
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7}>
                        <ArchCard>
                            <CardMedia
                                component="img"
                                image={DockerArchitectureImage}
                                alt="Docker Client-Host-Registry Architecture"
                                sx={{ objectFit: "contain", width: "100%", borderRadius: "8px" }}
                            />
                        </ArchCard>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <GlassCard>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: TEXT_WHITE }}>
                                The "Brain" (The Daemon)
                            </Typography>
                            <Typography variant="body1" sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8, opacity: 0.9 }}>
                                The <strong>Docker Daemon (dockerd)</strong> is the background process that
                                manages your containers, images, and networks.
                            </Typography>
                            <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 3 }} />
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TerminalIcon sx={{ color: DOCKER_BLUE }} />
                                    <Typography variant="body2" sx={{ color: TEXT_WHITE }}>The Client sends commands.</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <SettingsEthernetIcon sx={{ color: DOCKER_BLUE }} />
                                    <Typography variant="body2" sx={{ color: TEXT_WHITE }}>The Host executes them.</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2 }} >
                                    <HubIcon sx={{ color: DOCKER_BLUE }} />
                                    <Typography variant="body2" sx={{ color: TEXT_WHITE }}>The Registry stores the images.</Typography>
                                </Box>
                            </Stack>
                        </GlassCard>
                    </Grid>
                </Grid>

                {/* 3. THREE MAIN COMPONENTS (DRY APPROACH) */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                        The <span style={{ color: DOCKER_BLUE }}>Core Trio</span>
                    </Typography>
                    <Grid container spacing={3}>
                        {[
                            {
                                title: "The Client",
                                icon: <TerminalIcon />,
                                text: "The primary way users interact with Docker. When you type 'docker run', the client sends these commands to the dockerd."
                            },
                            {
                                title: "The Host",
                                icon: <StorageIcon />,
                                text: "Where the Docker Daemon lives. It manages the images, containers, networks, and volumes on your local machine or server."
                            },
                            {
                                title: "The Registry",
                                icon: <CloudQueueIcon />,
                                text: "A library for images. Docker Hub is the default public registry where you can pull (download) or push (upload) images."
                            }
                        ].map((component, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper sx={{
                                    p: 4,
                                    bgcolor: "rgba(255,255,255,0.02)",
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 4,
                                    height: "100%",
                                    transition: "0.3s",
                                    "&:hover": { transform: "translateY(-5px)", bgcolor: "rgba(13, 183, 237, 0.05)" }
                                }}>
                                    <Box sx={{ color: DOCKER_BLUE, mb: 2, transform: "scale(1.2)" }}>
                                        {component.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{component.title}</Typography>
                                    <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7, opacity: 0.7 }}>
                                        {component.text}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 6 }} />

                {/* 4. FOOTER / NAVIGATION */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3 }}>Next, let's get Docker running on your system.</Typography>
                    <Link to="/docker-installation" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 6,
                                py: 2,
                                borderRadius: "99px",
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Start Installation
                        </Button>
                    </Link>
                </Box>

            </Container>
        </PageWrapper>
    );
}
